import React, { useEffect, useContext, createContext, useState } from 'react';
import { Adsense as AdsenseComponent } from '@ctrl/react-adsense';

// Interface pour window.opera (maintenue pour compatibilité)
interface WindowWithOpera extends Window {
  opera?: unknown;
}

// Déclaration globale pour adsbygoogle
declare global {
  interface Window {
    adsbygoogle?: {
      push: (params: unknown) => void;
    };
    // La définition de gtag est déjà présente dans analytics.ts
    // Ne pas la redéfinir ici pour éviter les conflits
  }
}

// Composant ErrorBoundary pour capturer les erreurs dans les composants AdSense
class AdSenseErrorBoundary extends React.Component<
  { children: React.ReactNode, fallback?: React.ReactNode },
  { hasError: boolean, errorMessage: string }
> {
  constructor(props: { children: React.ReactNode, fallback?: React.ReactNode }) {
    super(props);
    this.state = { 
      hasError: false,
      errorMessage: ''
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { 
      hasError: true,
      errorMessage: error.message || 'Une erreur est survenue'
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('AdSense Error Boundary a capturé une erreur:', error, errorInfo);
    
    // Si c'est une erreur de removeChild, essayer de nettoyer le DOM
    if (error.message && error.message.includes('removeChild') && error.message.includes('not a child of this node')) {
      console.log('AdSense Error Boundary: Erreur de removeChild détectée, nettoyage du DOM');
      this.cleanupDOM();
    }
  }
  
  // Méthode pour nettoyer le DOM et éviter les erreurs futures
  cleanupDOM() {
    try {
      // Trouver tous les éléments ins.adsbygoogle
      const adsElements = document.querySelectorAll('ins.adsbygoogle');
      console.log(`AdSense Error Boundary: ${adsElements.length} éléments de publicité trouvés`);
      
      // Supprimer les éléments qui pourraient causer des problèmes
      adsElements.forEach((element) => {
        const parent = element.parentNode;
        if (parent && parent.contains(element)) {
          try {
            // Utiliser un cast pour éviter l'erreur de type
            if (parent instanceof HTMLElement) {
              parent.innerHTML = '';
              console.log('AdSense Error Boundary: Élément nettoyé avec succès');
            }
          } catch (e) {
            console.error('AdSense Error Boundary: Erreur lors du nettoyage de l\'élément', e);
          }
        }
      });
    } catch (error) {
      console.error('AdSense Error Boundary: Erreur lors du nettoyage du DOM', error);
    }
  }
  
  resetError = () => {
    this.setState({ hasError: false, errorMessage: '' });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="text-xs text-gray-400 p-2 text-center">
          <div>Publicité non disponible</div>
          <div className="mt-1 text-xs text-gray-300">{this.state.errorMessage}</div>
          <button 
            onClick={this.resetError}
            className="mt-2 text-xs text-blue-400 hover:text-blue-500"
          >
            Réessayer
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Contexte pour gérer l'état global des publicités AdSense
interface AdSenseContextType {
  initializedAds: Set<string>;
  registerAd: (id: string) => boolean;
  clearAds: () => void;
  pageLoadCount: number;
}

// Interface pour window.opera
interface WindowWithOpera extends Window {
  opera?: unknown;
}

// Nous utilisons la déclaration globale existante pour window.adsbygoogle
declare global {
  interface Window {
    adsbygoogle?: {
      push: (params: unknown) => void;
    };
    // La définition de gtag est déjà présente dans analytics.ts
    // Ne pas la redéfinir ici pour éviter les conflits
  }
}

const AdSenseContext = createContext<AdSenseContextType>({
  initializedAds: new Set<string>(),
  registerAd: () => false,
  clearAds: () => {},
  pageLoadCount: 0
});

// Composant pour charger le script AdSense et gérer le contexte global
export const AdSenseLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initializedAds] = useState<Set<string>>(new Set());
  const [pageLoadCount, setPageLoadCount] = useState(0);
  
  // Insérer le script AdSense dans le head lors du montage du composant
  useEffect(() => {
    // Vérifier si le script est déjà chargé
    if (!document.querySelector('script[src*="adsbygoogle.js"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4579146101917687';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
      console.log('AdSenseLoader: Script AdSense ajouté au head');
    } else {
      console.log('AdSenseLoader: Script AdSense déjà présent');
    }
  }, []);
  
  // Fonction pour enregistrer une publicité et éviter les doublons
  const registerAd = React.useCallback((id: string): boolean => {
    if (!id || id.includes('undefined')) {
      console.log(`AdSenseLoader: ID de publicité invalide: ${id}`);
      return false;
    }
    
    if (initializedAds.has(id)) {
      console.log(`AdSenseLoader: Publicité ${id} déjà initialisée, ignorée`);
      return false;
    }
    
    initializedAds.add(id);
    console.log(`AdSenseLoader: Publicité ${id} enregistrée`);
    
    // Suivre l'enregistrement avec Google Analytics si disponible
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'adsense_registered', {
        'event_category': 'Advertising',
        'event_label': id,
        'non_interaction': true
      });
    }
    
    return true;
  }, [initializedAds]);
  
  // Fonction pour nettoyer les publicités lors des changements de page
  const clearAds = React.useCallback(() => {
    // Suivre le nettoyage avec Google Analytics si disponible
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined' && initializedAds.size > 0) {
      window.gtag('event', 'adsense_cleanup', {
        'event_category': 'Advertising',
        'event_label': `ads_count_${initializedAds.size}`,
        'non_interaction': true
      });
    }
    
    initializedAds.clear();
    // Incrémenter le compteur pour forcer le rechargement des publicités
    setPageLoadCount(prev => prev + 1);
    console.log('AdSenseLoader: Nettoyage de toutes les publicités');
  }, [initializedAds]);
  
  // Écouter les changements de page pour nettoyer les publicités
  useEffect(() => {
    const handleRouteChange = () => {
      clearAds();
    };
    
    // Écouter les événements de navigation
    window.addEventListener('popstate', handleRouteChange);
    
    // Écouter les clics sur les liens pour détecter les changements de page
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link && link.href && !link.href.startsWith('javascript:') && !link.target) {
        // Si c'est un lien interne, nettoyer les publicités
        if (link.href.includes(window.location.hostname)) {
          clearAds();
        }
      }
    };
    
    document.addEventListener('click', handleLinkClick);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [clearAds]);

  return (
    <AdSenseErrorBoundary>
      <AdSenseContext.Provider value={{ initializedAds, registerAd, clearAds, pageLoadCount }}>
        {children}
      </AdSenseContext.Provider>
    </AdSenseErrorBoundary>
  );
};

interface AdSenseProps {
  className?: string;
  adSlot: string;
  adFormat?: string;
  fullWidthResponsive?: boolean;
  style?: React.CSSProperties;
  id?: string;
}

// Fonction utilitaire pour détecter si l'appareil est mobile
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Vérifier si c'est une PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as WindowWithOpera).opera;
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    typeof userAgent === 'string' ? userAgent.toLowerCase() : ''
  );
  const isSmallScreen = window.innerWidth < 768;
  
  // Si c'est une PWA ou un appareil mobile ou un petit écran, considérer comme mobile
  return isPWA || isMobileUA || isSmallScreen;
};

// Composant AdSense simplifié utilisant la bibliothèque
export const AdSense: React.FC<AdSenseProps> = ({
  className = '',
  adSlot,
  adFormat = 'auto',
  fullWidthResponsive = true,
  style,
  id
}) => {
  const { registerAd } = useContext(AdSenseContext);
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Générer un ID plus stable basé sur l'emplacement
  const adId = React.useMemo(() => {
    const prefix = `ad-${adSlot}`;
    const deviceType = isMobile ? 'mobile' : 'desktop';
    const pathId = typeof window !== 'undefined' ? 
      window.location.pathname.replace(/\//g, '-') : 'unknown';
    const uniqueId = id ? `-${id}` : '';
    return `${prefix}-${deviceType}-${pathId}${uniqueId}`;
  }, [adSlot, isMobile, id, typeof window !== 'undefined' ? window.location.pathname : '']);
  
  // Détection client/mobile
  useEffect(() => {
    setIsClient(true);

    const checkMobile = () => {
      const mobile = isMobileDevice();
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Suivre l'initialisation
  useEffect(() => {
    if (!isClient) return;

    // Enregistrer cette publicité
    registerAd(adId);
    
    // Suivre l'initialisation avec Google Analytics
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'adsense_initialized', {
        'event_category': 'Advertising',
        'event_label': adId,
        'non_interaction': true
      });
    }
  }, [isClient, adId, registerAd]);
  
  // Si nous sommes en rendu côté serveur, afficher un placeholder
  if (!isClient) {
    return (
      <div 
        id={adId}
        className={`adsense-container flex justify-center items-center ${className}`}
        style={{ 
          minHeight: '100px',
          display: 'flex',
          margin: '0 auto',
          ...style
        }}
      >
        <div style={{ textAlign: 'center', padding: '10px', color: '#ccc', fontSize: '12px' }}>
          Chargement de la publicité...
        </div>
      </div>
    );
  }
  
  // Dimensions adaptées selon le type d'appareil
  const adDimensions = isMobile ? {
    minHeight: '250px',
    maxWidth: '336px',
    width: '100%'
  } : {
    minHeight: '300px',
    maxWidth: '728px',
    width: '100%'
  };
  
  // Utiliser le composant de la bibliothèque
  return (
    <AdSenseErrorBoundary>
      <div id={adId} className={className} style={{ ...adDimensions, ...style }}>
        <AdsenseComponent
          client="ca-pub-4579146101917687"
          slot={adSlot}
          format={adFormat}
          responsive={fullWidthResponsive ? "true" : "false"}
          style={{ display: 'block', width: '100%' }}
        />
      </div>
    </AdSenseErrorBoundary>
  );
};

// Composant spécifique pour les annonces dans la liste des échanges
export const TradeListAd: React.FC = () => {
  const { pageLoadCount } = useContext(AdSenseContext);
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [adKey, setAdKey] = useState(Date.now());
  
  // Détecter si nous sommes côté client et si c'est un appareil mobile
  useEffect(() => {
    setIsClient(true);
    
    const checkMobile = () => {
      const mobile = isMobileDevice();
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Réagir aux changements de page pour forcer le remontage
  useEffect(() => {
    setAdKey(Date.now());
  }, [pageLoadCount]);
  
  // Si nous sommes en rendu côté serveur, afficher un placeholder
  if (!isClient) {
    return (
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-center"
        style={{ minHeight: '100px' }}
      >
        <div style={{ textAlign: 'center', padding: '10px', color: '#ccc', fontSize: '12px' }}>
          Chargement de la publicité...
        </div>
      </div>
    );
  }
  
  // Configuration pour les publicités in-article fluides dans la liste des échanges
  return (
    <AdSenseErrorBoundary>
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        key={adKey} // Forcer le remontage avec une clé unique
      >
        <AdsenseComponent
          client="ca-pub-4579146101917687"
          slot={isMobile ? '7719843388' : '1424794127'}
          layout="in-article"
          format="fluid"
          style={{ display: 'block' }}
        />
      </div>
    </AdSenseErrorBoundary>
  );
};

// Composant spécifique pour les bannières de conversation dans les messages
export const MessagesBannerAd: React.FC = () => {
  const { pageLoadCount } = useContext(AdSenseContext);
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [adKey, setAdKey] = useState(Date.now());
  
  // Détecter si nous sommes côté client et si c'est un appareil mobile
  useEffect(() => {
    setIsClient(true);
    
    const checkMobile = () => {
      const mobile = isMobileDevice();
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Réagir aux changements de page pour forcer le remontage
  useEffect(() => {
    setAdKey(Date.now());
  }, [pageLoadCount]);
  
  // Si nous sommes en rendu côté serveur, afficher un placeholder
  if (!isClient) {
    return (
      <div 
        className="w-full flex items-center justify-center"
        style={{ minHeight: '90px' }}
      >
        <div style={{ textAlign: 'center', padding: '10px', color: '#ccc', fontSize: '12px' }}>
          Chargement de la publicité...
        </div>
      </div>
    );
  }
  
  // Format de publicité selon le type d'appareil
  return (
    <AdSenseErrorBoundary>
      <div 
        className="w-full overflow-hidden"
        key={adKey} // Forcer le remontage avec une clé unique
      >
        <AdsenseComponent
          client="ca-pub-4579146101917687"
          slot="1424794127"
          format={isMobile ? "horizontal" : "horizontal"}
          responsive="true"
          style={{ 
            display: 'block', 
            width: '100%',
            minHeight: isMobile ? '50px' : '90px'
          }}
        />
      </div>
    </AdSenseErrorBoundary>
  );
}; 